import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { Session } from '@/api/client';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { getFontSizeLevelLabel } from './fontSizeUtils';
import '@/components/ProjectsTable/EditableCell.css';
import '@/components/ProjectsTable/ColumnWidthPopover.css';
import './FontSizeSelector.css';

type FontSizeField = 'font_size' | 'second_project_font_size';

interface FontSizeSelectorProps {
  session: Session;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSessionUpdate: (id: string, data: { font_size?: number; second_project_font_size?: number }) => Promise<unknown>;
  onSessionOptimisticUpdate: (id: string, data: { font_size?: number; second_project_font_size?: number }) => void;
  onFontSizeChange?: (level: number) => void;
  autoClose?: boolean;
  /** Поле для обновления: font_size (основная зона) или second_project_font_size (разделённая зона). По умолчанию font_size. */
  field?: FontSizeField;
}

function getValueForField(session: Session, field: FontSizeField): number {
  if (field === 'second_project_font_size') {
    const raw = session.second_project_font_size ?? 3;
    return Math.max(0, Math.min(5, raw));
  }
  const raw = session.font_size ?? 3;
  return Math.max(0, Math.min(5, raw));
}

export function FontSizeSelector({
  session,
  anchorEl,
  open,
  onClose,
  onSessionUpdate,
  onSessionOptimisticUpdate,
  onFontSizeChange,
  autoClose = false,
  field = 'font_size',
}: FontSizeSelectorProps) {
  const defaultLevel = getValueForField(session, field);
  const [localLevel, setLocalLevel] = useState(defaultLevel);
  const [originalLevel, setOriginalLevel] = useState(defaultLevel);
  const popoverRef = useRef<HTMLDivElement>(null);

  const buildPatch = useCallback((level: number) => (
    field === 'second_project_font_size' ? { second_project_font_size: level } : { font_size: level }
  ), [field]);

  const handleRequestClose = useCallback(async () => {
    if (localLevel !== originalLevel) {
      try {
        await onSessionUpdate(session.id, buildPatch(localLevel));
        setOriginalLevel(localLevel);
      } catch {
        onSessionOptimisticUpdate(session.id, buildPatch(originalLevel));
      }
    }
    onClose();
  }, [localLevel, originalLevel, session.id, onSessionUpdate, onSessionOptimisticUpdate, onClose, buildPatch]);

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    open,
    handleRequestClose,
    true
  );

  const handlePointerEnd = useCallback(async () => {
    if (localLevel !== originalLevel) {
      try {
        await onSessionUpdate(session.id, buildPatch(localLevel));
        setOriginalLevel(localLevel);
      } catch {
        onSessionOptimisticUpdate(session.id, buildPatch(originalLevel));
      }
    }
    if (autoClose) {
      requestClose();
    }
  }, [localLevel, originalLevel, session.id, onSessionUpdate, onSessionOptimisticUpdate, autoClose, requestClose, buildPatch]);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    horizontalAlign: 'center',
    offset: 8,
    zIndex: 1001,
    dependencies: [session.id],
  });

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    enabled: shouldRender,
    capture: false,
  });

  /** Только открытие попапа / смена сессии или поля. Не подписываемся на session.font_size:
   * после optimistic update родитель отдаёт новый font_size и эффект затирал originalLevel → откат при ошибке ломался. */
  useEffect(() => {
    if (!open) return;
    const level = getValueForField(session, field);
    setLocalLevel(level);
    setOriginalLevel(level);
    // session / getValueForField — снимок при смене open | session.id | field
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно не реагируем на optimistic патчи font_size
  }, [open, session.id, field]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newLevel = Math.max(0, Math.min(5, parseInt(e.target.value, 10)));
      setLocalLevel(newLevel);
      onSessionOptimisticUpdate(session.id, field === 'second_project_font_size' ? { second_project_font_size: newLevel } : { font_size: newLevel });
      onFontSizeChange?.(newLevel);
    },
    [session.id, onSessionOptimisticUpdate, onFontSizeChange, field]
  );

  const hasAnchor = !!anchorEl;
  const anchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const panelStyle: React.CSSProperties = {
    width: 220,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  if (!shouldRender) return null;

  const sizeLabel = getFontSizeLevelLabel(localLevel);

  return createPortal(
    <div ref={popoverRef} style={anchorStyle}>
      <OverlayIntrinsicSize>
        <div style={panelStyle} className={`font-size-popover editable-cell-popover ${contentClasses}`}>
          <div className="editable-cell-popover__header">
            {field === 'second_project_font_size' ? (
              <>
                Размер шрифта следующего проекта:{' '}
                <span className="editable-cell-popover__header-accent">{sizeLabel}</span>
              </>
            ) : (
              <>
                Размер шрифта: <span className="editable-cell-popover__header-accent">{sizeLabel}</span>
              </>
            )}
          </div>
          <div className="editable-cell-popover__form">
            <input
              type="range"
              min={0}
              max={5}
              step={1}
              value={localLevel}
              onChange={handleChange}
              onMouseUp={handlePointerEnd}
              onTouchEnd={handlePointerEnd}
              className="app-range-slider"
              aria-label={`Размер шрифта, ${sizeLabel}`}
            />
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
